import React, { useState, useEffect, useRef } from 'react';
import { Car, FileCheck, CalendarCheck, Heart } from 'lucide-react';

// Configurazione contatori - Facile da aggiornare o collegare a API esterna
const COUNTER_DATA = [
  {
    id: 'rca',
    icon: Car,
    value: 409,
    suffix: '+',
    label: "Richieste RCA 'Basta la Targa' gestite con successo",
    color: '#E74C3C',
    bgColor: 'bg-[#E74C3C]/10'
  },
  {
    id: 'quotazioni',
    icon: FileCheck,
    value: 283,
    suffix: '+',
    label: "Schede di Quotazione e Analisi Rischi per Cooperative e Enti No-Profit",
    color: '#0E8C84',
    bgColor: 'bg-[#0E8C84]/10'
  },
  {
    id: 'consulenze',
    icon: CalendarCheck,
    value: 44,
    suffix: '+',
    label: "Consulenze specialistiche prenotate online",
    color: '#1B2380',
    bgColor: 'bg-[#1B2380]/10'
  },
  {
    id: 'ore_gratuite',
    icon: Heart,
    value: 160,
    suffix: '+',
    label: "Ore di Consulenza Gratuita donate agli Enti del Terzo Settore",
    color: '#0E8C84',
    bgColor: 'bg-[#0E8C84]/10',
    highlight: true
  }
];

const AnimatedCounter = ({ value, suffix, duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * value));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [value, duration, isVisible]);
  
  // Format number with dots for thousands
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };
  
  return (
    <span className="tabular-nums">
      {formatNumber(count)}{suffix}
    </span>
  );
};

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_HEADER = {
  title: 'Il Nostro Impegno per il Terzo Settore',
  subtitle: 'Numeri che raccontano il nostro impatto quotidiano nel supportare chi opera per il bene comune.',
  date_note: 'Dal 21.02.2024 ad oggi'
};

const ImpactCounter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [header, setHeader] = useState(DEFAULT_HEADER);
  const [counters, setCounters] = useState(COUNTER_DATA);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/impact`)
      .then((r) => r.json())
      .then((data) => {
        if (data.impact) {
          setHeader({
            title: data.impact.title || DEFAULT_HEADER.title,
            subtitle: data.impact.subtitle || DEFAULT_HEADER.subtitle,
            date_note: data.impact.date_note || DEFAULT_HEADER.date_note
          });
          if (Array.isArray(data.impact.counters)) {
            setCounters(COUNTER_DATA.map((base) => {
              const saved = data.impact.counters.find((c) => c.id === base.id);
              return saved ? { ...base, value: saved.value, label: saved.label } : base;
            }));
          }
        }
      })
      .catch(() => {});
  }, []);

  const renderTitle = () => {
    const idx = header.title.indexOf('Terzo Settore');
    if (idx === -1) return header.title;
    return (
      <>
        {header.title.slice(0, idx)}
        <span className="text-[#8FE3DA]">Terzo Settore</span>
        {header.title.slice(idx + 'Terzo Settore'.length)}
      </>
    );
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only animate once
        }
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <section 
      ref={sectionRef}
      className="relative py-16 sm:py-20 overflow-hidden"
      id="impact"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#10154F] via-[#1B2380] to-[#10154F]"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDI0di0yaDEyek0zNiAyNnYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white/90 text-sm font-medium mb-4 border border-white/20">
            Societa Benefit - Impatto Sociale
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {renderTitle()}
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-2">
            {header.subtitle}
          </p>
          <p className="text-white/60 text-sm">
            {header.date_note}
          </p>
        </div>
        
        {/* Counters Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {counters.map((item, index) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className={`relative group ${
                  item.highlight 
                    ? 'bg-gradient-to-br from-[#0E8C84]/20 to-[#0E8C84]/20 border-2 border-[#0E8C84]/50' 
                    : 'bg-white/5 border border-white/10'
                } backdrop-blur-md rounded-2xl p-6 sm:p-8 text-center transform transition-all duration-500 hover:scale-105 hover:bg-white/10`}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Highlight Badge */}
                {item.highlight && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#0E8C84] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                      Societa Benefit
                    </span>
                  </div>
                )}
                
                {/* Icon */}
                <div 
                  className={`w-14 h-14 sm:w-16 sm:h-16 ${item.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon 
                    size={28} 
                    style={{ color: item.color }}
                    className="sm:w-8 sm:h-8"
                  />
                </div>
                
                {/* Number */}
                <div 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ color: item.highlight ? '#0E8C84' : '#8FE3DA' }}
                >
                  <AnimatedCounter 
                    value={item.value} 
                    suffix={item.suffix}
                    isVisible={isVisible}
                    duration={2000 + (index * 200)}
                  />
                </div>
                
                {/* Label */}
                <p className="text-white/80 text-sm sm:text-base leading-relaxed">
                  {item.label}
                </p>
              </div>
            );
          })}
        </div>
        
        {/* Benefit Statement */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-block bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 max-w-3xl">
            <p className="text-white/90 italic text-base sm:text-lg leading-relaxed">
              "In quanto <strong className="text-[#0E8C84]">Societa Benefit</strong>, Assietica trasforma la competenza 
              assicurativa in valore sociale, offrendo supporto gratuito agli enti che operano per il bene comune."
            </p>
            <div className="mt-4 flex items-center justify-center space-x-2">
              <Heart size={18} className="text-[#0E8C84]" />
              <span className="text-[#0E8C84] font-semibold text-sm">Assietica Soc. Coop. Societa Benefit</span>
            </div>
          </div>
        </div>
        
        {/* Note for future API integration */}
        {/* 
          NOTA TECNICA: I valori in COUNTER_DATA possono essere facilmente 
          sostituiti con chiamate API (es. Google Sheets, Airtable, MongoDB)
          usando useEffect per fetchare i dati dinamicamente.
          
          Esempio:
          useEffect(() => {
            fetch('/api/stats')
              .then(res => res.json())
              .then(data => setCounterData(data));
          }, []);
        */}
      </div>
    </section>
  );
};

export default ImpactCounter;
